import React, { useState } from "react";

import { Panel } from "./Panel";
import ImagesModal from "../../../Pages/Shipper/Customer/MainPages/Orders/Components/OrderHistoryModal/ImagesModal/ImagesModal";
import { checkFileType, convertTime } from "../../../helpers/helpers";
import { AttachmentChip } from "./AttachmentChip";
import { effectiveRevisions } from "../writes/chargeModel";
import {
  MdOutlineDescription,
  MdAttachFile,
  MdVisibility,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

/**
 * Notes — a compact activity feed, newest first. One row per charge revision
 * that carried a note, a file, or both.
 *
 * Read as a feed rather than a table: an admin scanning a disputed invoice
 * wants what changed, when, and why in one line, not a stack of boxes.
 *
 * No actor is shown. A charge row carries no author, and the only source is a
 * timestamp correlation against activity_log, which yields a bare causer id
 * ("user 1") for anyone not in the payload — noise rather than information.
 *
 * WHO SEES THIS — traced through the render chain, not inferred from labels:
 * both the note text AND its `meta === "admin"` attachments are rendered by
 * NotesUser.jsx:86 inside its default `type = "Show"` branch, mounted as
 * `<Notes data={data} />` at StaticInvoice.jsx:182. That block sits OUTSIDE
 * the `actions != false` guard and OUTSIDE the role gate at
 * StaticInvoice.jsx:170-171, so it reaches every authenticated role that can
 * open an invoice — customer, Carrier and warehouseUser — even on the four
 * screens that pass `actions={false}`.
 *
 * The `meta === "admin"` tag is therefore misleading: it does NOT mean
 * internal. `meta === "user"` charge attachments are rendered nowhere at all.
 *
 * NOT customer-visible: the PDF. PrintableInvoice passes `type="Print"`, and
 * that branch (NotesUser.jsx:127-132) renders the description only.
 *
 * The "included in the email notification" promise in production's composer
 * placeholder is a BACKEND claim the frontend cannot confirm, so it is not
 * repeated as fact here.
 */

const FIXED_KEYS = [
  ["base_price", "Base rate"],
  ["fuel_charge", "Fuel surcharge"],
  ["freight_charge", "Freight charge"],
  ["signature_charge", "Signature"],
  ["residential_charge", "Residential delivery"],
  ["PickupCharge", "Pickup charge"],
  ["insurance_charge", "Insurance charge"],
];

const num = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** What this revision changed, for the row title. */
const changedLabel = (row) => {
  const parts = FIXED_KEYS.filter(([key]) => num(row?.[key]) !== 0).map(
    ([key, label]) =>
      i18n.t(`admin:invoice.lines.${key}`, { defaultValue: label }),
  );
  (row?.additional_charges || []).forEach((c) => {
    if (num(c?.Charge) !== 0 && c?.NameEn) parts.push(c.NameEn);
  });
  return parts.join(" · ");
};

const RevisionNotesPanel = ({ data }) => {
  const { t } = useTranslation("admin");
  const [fullview, setFullview] = useState();

  // same rows, and so the same revision numbers, as the ledger
  const entries = effectiveRevisions(data?.shipment_charges).list
    .map((charge, index) => ({ charge, revision: index + 1 }))
    .filter(
      ({ charge }) =>
        charge?.description ||
        (charge?.media || []).some((m) => m?.meta === "admin"),
    )
    .reverse(); // newest first

  if (!entries.length) return null;

  return (
    <>
      <ImagesModal setOpen={setFullview} open={fullview} />

      <Panel
        title={t("invoice.notes")}
        chip={entries.length}
        actions={
          <span
            title={t("invoice.notesVisibleHint")}
            className="inline-flex items-center gap-1.5 font-manrope text-[10.5px] font-semibold text-ui-text-muted"
          >
            <MdVisibility className="h-3.5 w-3.5" />
            {t("invoice.visibleToCustomer")}
          </span>
        }
      >
        <ol className="m-0 list-none p-0">
          {entries.map(({ charge, revision }) => {
            const attachments = (charge?.media || []).filter(
              (m) => m?.meta === "admin",
            );
            const label = changedLabel(charge);

            return (
              <li
                key={charge?.id ?? revision}
                className="flex min-h-[32px] flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-ui-border-subtle px-2.5 py-1.5 first:border-t-0"
              >
                <span
                  title={attachments.length && !charge?.description ? t("invoice.attachment") : t("invoice.note")}
                  className="flex h-5 w-5 flex-none items-center justify-center rounded-full border border-ui-border bg-ui-surface text-ui-text-muted"
                >
                  {attachments.length && !charge?.description ? (
                    <MdAttachFile className="h-[11px] w-[11px]" />
                  ) : (
                    <MdOutlineDescription className="h-[11px] w-[11px]" />
                  )}
                </span>

                {label && (
                  <span className="flex-none font-manrope text-[11.5px] font-bold text-ui-text">
                    {label}
                  </span>
                )}

                <span
                  className={`min-w-0 flex-[1_1_160px] font-manrope text-[11.5px] leading-[1.4] [text-wrap:pretty] ${
                    charge?.description
                      ? "font-medium text-ui-text-secondary"
                      : "italic font-medium text-ui-text-muted"
                  }`}
                >
                  {charge?.description || t("invoice.noNoteWritten")}
                </span>

                {attachments.map((attachment) => {
                  const isImage = checkFileType(attachment?.file) === "Image";
                  const name = attachment?.orignal_file_name || "attachment";
                  return (
                    <AttachmentChip
                      key={attachment?.id}
                      name={name}
                      meta={(name.split(".").pop() || "").toUpperCase()}
                      isImage={isImage}
                      thumbUrl={isImage ? attachment?.file : null}
                      downloadHref={attachment?.file}
                      onPreview={() =>
                        isImage
                          ? setFullview(attachment?.file || "")
                          : window.open(attachment?.file, "_blank")
                      }
                    />
                  );
                })}

                <span className="ml-auto flex flex-none items-center gap-2.5 whitespace-nowrap font-manrope text-[10.5px] font-medium text-ui-text-muted">
                  <span className="font-mono text-[10px]">
                    {t("invoice.revShort", { rev: revision })}
                  </span>
                  <span className="tabular-nums">
                    {convertTime(charge?.created_at)}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      </Panel>
    </>
  );
};

export default RevisionNotesPanel;
